/**
 * useGeometryHover Hook
 *
 * Provides hover highlighting for geometry layers using QueryRenderedFeatures strategy.
 * Updates cursor and sets hovered state in Zustand.
 * Works in both edit mode (form open) and view mode (form closed).
 *
 * Strategy (QueryRenderedFeatures):
 * - Uses global mousemove event instead of layer-specific mouseenter/mouseleave
 * - Queries rendered features at mouse position (same as drag hooks)
 * - More reliable than layer events which don't always fire
 *
 * Features:
 * - Changes cursor (pointer/grab) based on isDraggable
 * - Updates Zustand hoveredGeometryId state
 * - Returns isHovered for visual styling
 * - Works in both edit and view modes (disabled only when drawing)
 * - Works with MapLibre GL JS queryRenderedFeatures
 *
 * Usage:
 * - Works with MapLibre GL JS interactive layers (Line, Shape)
 * - For Marker, use direct HTML event listeners
 *
 * @module hooks/useGeometryHover
 * @example
 * ```typescript
 * const { isHovered } = useGeometryHover({
 *   geometryId: geometry.id,
 *   interactiveLayerId: `line-hitbox-${geometry.id}`,
 *   isDraggable: isLineDraggable,
 *   isPreview: false,
 * });
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import type { MapMouseEvent } from 'maplibre-gl';
import { useGeoMarkupStore } from '../store';
import type { Coordinates } from '../types';

/**
 * Options for useGeometryHover hook
 */
export interface UseGeometryHoverOptions {
  /** ID of the geometry */
  geometryId: string;
  /** ID of the interactive layer (hitbox layer) */
  interactiveLayerId: string;
  /** Whether the geometry is draggable (affects cursor) */
  isDraggable: boolean;
  /** Whether this is a preview geometry */
  isPreview?: boolean;
}

/**
 * Hook for handling geometry hover highlighting
 *
 * Uses global mousemove event + queryRenderedFeatures for reliable hover detection.
 * Same strategy as useLineDrag/useShapeDrag for consistency.
 *
 * @param options - Hook configuration options
 * @returns Object with isHovered boolean for styling
 */
export const useGeometryHover = ({
  geometryId,
  interactiveLayerId,
  isDraggable,
  isPreview = false,
}: UseGeometryHoverOptions) => {
  const { current: map } = useMap();

  // Zustand state (atomic selectors)
  const isDrawing = useGeoMarkupStore((s) => s.isDrawing);
  const hoveredGeometryId = useGeoMarkupStore((s) => s.hoveredGeometryId);

  // Zustand action (stable reference)
  const setHoveredGeometry = useGeoMarkupStore((s) => s.setHoveredGeometry);

  // Check if this geometry is currently hovered
  const isHovered = hoveredGeometryId === geometryId;

  // Track if we're currently hovering this geometry (local state for cleanup)
  const isCurrentlyHoveringRef = useRef(false);

  // Cursor anchor for the hover popup — tracking the cursor (vs static
  // `calculatedCenter`) keeps the tooltip visible when the geometry's
  // center sits outside the viewport at high zoom.
  const [hoverPoint, setHoverPoint] = useState<Coordinates | null>(null);

  /**
   * Setup global mousemove event listener with queryRenderedFeatures
   */
  useEffect(() => {
    if (!map || isPreview) return;

    /**
     * Handle mouse move - check if mouse is over this geometry
     * Uses queryRenderedFeatures to detect if cursor is over interactive layer
     */
    const handleMouseMove = (e: MapMouseEvent) => {
      // Only check hover when NOT drawing (works in both edit and view modes)
      if (!isDrawing) {
        // ✅ Guard: Check if layer exists before querying (prevents errors during component remounting)
        if (!map.getLayer(interactiveLayerId)) return;

        // Query features at mouse position
        const features = map.queryRenderedFeatures(e.point, {
          layers: [interactiveLayerId],
        });

        const isOver = features.length > 0;

        if (isOver) {
          setHoverPoint({ lat: e.lngLat.lat, lng: e.lngLat.lng });
          if (!isCurrentlyHoveringRef.current) {
            // Mouse entered geometry
            isCurrentlyHoveringRef.current = true;
            setHoveredGeometry(geometryId);
            map.getCanvas().style.cursor = isDraggable ? 'grab' : 'pointer';
          }
        } else if (isCurrentlyHoveringRef.current) {
          // Mouse left geometry
          isCurrentlyHoveringRef.current = false;
          setHoveredGeometry(null);
          setHoverPoint(null);
          map.getCanvas().style.cursor = '';
        }
      } else if (isCurrentlyHoveringRef.current) {
        // Drawing started - clear hover
        isCurrentlyHoveringRef.current = false;
        setHoveredGeometry(null);
        setHoverPoint(null);
        map.getCanvas().style.cursor = '';
      }
    };

    // Register global mousemove event
    map.on('mousemove', handleMouseMove);

    return () => {
      // Cleanup: remove event listener and clear hover state if needed
      map.off('mousemove', handleMouseMove);

      if (isCurrentlyHoveringRef.current) {
        isCurrentlyHoveringRef.current = false;
        setHoveredGeometry(null);
        setHoverPoint(null);
        map.getCanvas().style.cursor = '';
      }
    };
  }, [
    map,
    interactiveLayerId,
    geometryId,
    isDrawing,
    isDraggable,
    isPreview,
    setHoveredGeometry,
  ]);

  return {
    /** Whether this geometry is currently hovered */
    isHovered,
    /** Live cursor position (lngLat) while hovering this geometry, else null. */
    hoverPoint,
  };
};
